import { Button } from 'components/atoms/Button';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import ModalHeading from 'components/molecules/ModalHeading';

// Lottie animations reference images by filename (e.g. "O.png") with a relative
// base path (the "u" field). The bundler doesn't know about these references, so
// the images would 404 at runtime. We statically import each image to get the
// bundler-resolved URL, then map original filenames to those URLs.
import imageO from './images/O.png';
import imageM from './images/M.png';
import imageP from './images/P.png';
import imageN from './images/N.png';
import imageV from './images/v.png';
import imageQ from './images/q.png';

// Maps original Lottie asset filenames → bundler-resolved URLs
const IMAGE_MAP: Record<string, string> = {
  'O.png': imageO,
  'M.png': imageM,
  'P.png': imageP,
  'N.png': imageN,
  'v.png': imageV,
  'q.png': imageQ,
};

/**
 * Lottie JSON stores image assets with two fields:
 *   - "u" (base path, e.g. "/images/")
 *   - "p" (filename, e.g. "O.png")
 *
 * At runtime, the Lottie renderer concatenates u + p to load images.
 * Since we bundle images via static imports, we need to:
 *   1. Clear "u" so the renderer doesn't prepend a broken path
 *   2. Replace "p" with the bundler-resolved absolute URL
 *
 * Typed as Record<string, unknown> because Lottie JSON is an external format
 * we don't own — runtime guards handle the actual shape validation.
 */
const patchAnimationImages = (data: Record<string, unknown>) => {
  const cloned = structuredClone(data);
  const assets = cloned.assets;

  if (!Array.isArray(assets)) return cloned;

  cloned.assets = assets.map((asset: Record<string, unknown>) => {
    const filename = typeof asset.p === 'string' ? asset.p : null;
    if (filename && IMAGE_MAP[filename]) {
      return { ...asset, u: '', p: IMAGE_MAP[filename] };
    }
    return asset;
  });

  return cloned;
};

const PeppolBanking: FC<{ onConfirm?: () => void }> = ({ onConfirm }) => {
  const { t } = useTranslation();
  const [animationData, setAnimationData] = useState<Record<
    string,
    unknown
  > | null>(null);

  // Dynamic import keeps the animation JSON out of the main bundle.
  // Once loaded, we patch image paths so the Lottie renderer can find them.
  useEffect(() => {
    import('./animations/main.json').then((module) => {
      const patched = patchAnimationImages(module.default);
      setAnimationData(patched);
    });
  }, []);

  return (
    <div className="flex items-center justify-center flex-col relative pb-20">
      <ModalHeading
        title={t('accountable_banking.onboarding.peppol.title')}
        titleClassName="font-degular"
        subtitle={t('accountable_banking.onboarding.peppol.subtitle')}
        subtitleClassName="w-[80%]"
      />
      <div className="flex justify-center mb-8" style={{ minHeight: 220 }}>
        {animationData && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {
                  preserveAspectRatio: 'xMidYMid meet',
                },
              }}
              height={220}
              width={358}
            />
          </motion.div>
        )}
      </div>
      <Button
        color="bank"
        className="mt-6 w-[80%] absolute bottom-0"
        onClick={onConfirm}
      >
        {t('continue')}
      </Button>
    </div>
  );
};

export default PeppolBanking;
